import { Component, Fragment, useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import axios from 'axios';

import {
  Button,
  Checkbox,
  Form,
  Icon,
  Input,
  Label,
  Segment,
} from 'semantic-ui-react';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  List,
  Heading,
  Link,
} from 'ckeditor5';

import {
  CustomTable,
  SlideoutPanel,
  WithBodyScrollLock,
} from '../../../components';

import DateRangeChart from '../../../components/dateRangeChart';
import { UserSearchDropdown } from '../../../components/users';
import { OrganisationDropdown } from '../../../components/organisations';
import { authMsg } from '../../../utils/auth';
import { getCommonError } from '../../../utils';

import './EmailLogs.scss';

const LIMIT = 25;
// TODO:tz hardcoded until organisation settings has a timezone field to read instead
const DISPLAY_TIMEZONE = 'Australia/Melbourne';

const formatDateWithTimezone = (date) =>
  moment(date).tz(DISPLAY_TIMEZONE).format('ll HH:mm:ss z');

const STATUS_COLOR = {
  queued: 'grey',
  sent: 'blue',
  send: 'green',
  delivery: 'green',
  bounce: 'red',
  complaint: 'red',
  reject: 'red',
  failed: 'red',
};

// Auto-resizes the iframe to fit its content: `allow-same-origin` (but not
// `allow-scripts`) lets us read the rendered document's height without
// letting any script in the email HTML execute.
const EmailHtmlPreview = ({ html }) => {
  const { t } = this.props;
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(200);

  useEffect(() => {
    setHeight(200);
  }, [html]);

  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) {
      return;
    }
    const measured = Math.max(
      doc.documentElement.scrollHeight,
      doc.body?.scrollHeight || 0
    );
    setHeight(Math.max(measured, 100));
  };

  return (
    <iframe
      ref={iframeRef}
      className="email-log-info-iframe"
      srcDoc={html}
      sandbox="allow-same-origin"
      title={t('admin.email_logs.email_content_preview')}
      onLoad={handleLoad}
      style={{ height }}
    />
  );
};

/**********
const SingleLogViewerSlideout = (props) => {

  return
    <SlideoutPanel
      open={props.open}
      onClose={props.onClose}
      title="Email Logs - Details"
      className="email-log-info-panel"
    >
      {selectedLog && (
        <>
          <div className="email-log-info-table-content">
            <div className="email-log-info-field">
              <label>To</label>
              <div>{selectedLog.toEmail || '-'}</div>
            </div>
            <div className="email-log-info-field">
              <label>Subject</label>
              <div>{selectedLog.subject || '-'}</div>
            </div>
          </div>
          {selectedLog.html ? (
            <div className="email-log-info-iframe-frame">
              <EmailHtmlPreview html={selectedLog.html} />
            </div>
          ) : (
            <div className="email-log-info-no-content">
              No HTML content available for this email.
            </div>
          )}
        </>
      )}
    </SlideoutPanel>;
};
***********/

class EmailLogs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      docs: [],
      total: 0,
      pages: 0,
      loading: false,
      currentPage: 1,
      startDate: null,
      endDate: null,
      // applied filters — these drive fetchEmailLogs
      toUser: null,
      subject: '',
      org: null,
      status: [],
      // draft filters — only used while the Advanced Search panel is open;
      // copied into the applied filters above on "Apply", discarded otherwise
      draftToUser: null,
      draftSubject: '',
      draftOrg: null,
      draftStatus: [],
      showAdvancedSearch: false,
      statusOptions: [],
      selectedLog: null,
      showInfoDetail: false,
      showSendTestEmail: false,
      testEmailTo: '',
      testEmailSubject: '',
      testEmailMessage: '',
      sendingTestEmail: false,
    };
  }

  componentDidMount() {
    this.fetchEmailLogs(1);
    this.fetchStatusOptions();
  }

  fetchStatusOptions = async () => {
    try {
      const { data } = await axios.get('/email/statuses');
      this.setState({ statusOptions: data });
    } catch (error) {
      authMsg('error', getCommonError(error));
    }
  };

  fetchEmailLogs = async (
    page,
    startDate = this.state.startDate,
    endDate = this.state.endDate,
    toUser = this.state.toUser,
    subject = this.state.subject,
    org = this.state.org,
    status = this.state.status
  ) => {
    this.setState({ loading: true });
    try {
      const params = { limit: LIMIT, page, order: 'desc' };
      if (startDate) {
        params.startDate = startDate.format('YYYY-MM-DD');
      }
      if (endDate) {
        params.endDate = endDate.format('YYYY-MM-DD');
      }
      if (toUser) {
        params.toEmail = toUser.email;
      }
      if (subject) {
        params.subject = subject;
      }
      if (org) {
        params.orgId = org.id;
      }
      if (status.length > 0) {
        params.status = status.join(',');
      }
      const { data } = await axios.get('/admin/email-logs', { params });
      this.setState({
        ...data,
        currentPage: page,
        startDate,
        endDate,
        toUser,
        subject,
        org,
        status,
        loading: false,
      });
    } catch (error) {
      this.setState({ loading: false });
      authMsg('error', getCommonError(error));
    }
  };

  pageChange = (page) => {
    this.fetchEmailLogs(page);
  };

  handleDateRangeChange = (event, picker) => {
    this.fetchEmailLogs(1, picker.startDate, picker.endDate);
  };

  clearDateRange = () => {
    this.fetchEmailLogs(1, null, null);
  };

  refresh = () => {
    this.fetchEmailLogs(this.state.currentPage);
  };

  openAdvancedSearch = () => {
    // seed the draft from whatever is currently applied
    this.props.lockScroll();
    this.setState((prevState) => ({
      draftToUser: prevState.toUser,
      draftSubject: prevState.subject,
      draftOrg: prevState.org,
      draftStatus: prevState.status,
      showAdvancedSearch: true,
    }));
  };

  cancelAdvancedSearch = () => {
    // discard the draft — reset it back to the applied values and close
    this.props.unlockScroll();
    this.setState((prevState) => ({
      draftToUser: prevState.toUser,
      draftSubject: prevState.subject,
      draftOrg: prevState.org,
      draftStatus: prevState.status,
      showAdvancedSearch: false,
    }));
  };

  resetAdvancedSearch = () => {
    // clears the draft fields only — panel stays open, nothing is applied
    // until the user clicks Apply
    this.setState({
      draftToUser: null,
      draftSubject: '',
      draftOrg: null,
      draftStatus: [],
    });
  };

  applyAdvancedSearch = () => {
    this.props.unlockScroll();
    const {
      draftToUser,
      draftSubject,
      draftOrg,
      draftStatus,
      startDate,
      endDate,
    } = this.state;
    this.setState({ showAdvancedSearch: false });
    this.fetchEmailLogs(
      1,
      startDate,
      endDate,
      draftToUser,
      draftSubject,
      draftOrg,
      draftStatus
    );
  };

  handleDraftToUserChange = (toUser) => {
    this.setState({ draftToUser: toUser });
  };

  handleDraftSubjectChange = (event) => {
    this.setState({ draftSubject: event.target.value });
  };

  handleDraftOrgChange = (org) => {
    this.setState({ draftOrg: org });
  };

  handleDraftStatusToggle = (status) => {
    this.setState((prevState) => ({
      draftStatus: prevState.draftStatus.includes(status)
        ? prevState.draftStatus.filter((s) => s !== status)
        : [...prevState.draftStatus, status],
    }));
  };

  openInfoDetail = (log) => {
    this.props.lockScroll();
    this.setState({ selectedLog: log, showInfoDetail: true });
  };

  closeInfoDetail = () => {
    this.props.unlockScroll();
    this.setState({ showInfoDetail: false });
  };

  openSendTestEmail = () => {
    this.props.lockScroll();
    this.setState({
      testEmailTo: this.props.userEmail || '',
      testEmailSubject: '',
      testEmailMessage: '',
      showSendTestEmail: true,
    });
  };

  closeSendTestEmail = () => {
    this.props.unlockScroll();
    this.setState({ showSendTestEmail: false });
  };

  handleTestEmailToChange = (event) => {
    this.setState({ testEmailTo: event.target.value });
  };

  handleTestEmailSubjectChange = (event) => {
    this.setState({ testEmailSubject: event.target.value });
  };

  handleTestEmailMessageChange = (event, editor) => {
    this.setState({ testEmailMessage: editor.getData() });
  };

  sendTestEmail = async () => {
    const { t } = this.props;
    const { testEmailTo, testEmailSubject, testEmailMessage } = this.state;
    this.setState({ sendingTestEmail: true });
    try {
      await axios.post('/admin/send-test-email', {
        to: testEmailTo,
        subject: testEmailSubject,
        message: testEmailMessage,
      });
      authMsg('success', t('admin.email_logs.test_email_queued'));
      this.props.unlockScroll();
      this.setState({ sendingTestEmail: false, showSendTestEmail: false });
    } catch (error) {
      this.setState({ sendingTestEmail: false });
      authMsg('error', getCommonError(error));
    }
  };

  get columns() {
    const { t } = this.props;

    return [
      {
        title: <strong>{t('common.date')}</strong>,
        render: (data) => formatDateWithTimezone(data.createdAt),
        width: 2,
      },
      {
        title: <strong>{t('common.organisation')}</strong>,
        render: (data) => data.organisation?.name || '-',
        width: 2,
      },
      {
        title: <strong>{t('admin.email_logs.to')}</strong>,
        render: (data) => data.toEmail || '-',
        width: 3,
      },
      {
        title: <strong>{t('admin.email_logs.subject')}</strong>,
        render: (data) => data.subject || '-',
        width: 4,
      },
      {
        title: <strong>{t('admin.email_logs.template')}</strong>,
        render: (data) => data.template || '-',
        width: 2,
      },
      {
        title: <strong>{t('admin.email_logs.context')}</strong>,
        render: (data) => data.context || '-',
        width: 2,
      },
      {
        title: <strong>{t('admin.email_logs.status')}</strong>,
        render: (data) => (
          <Label color={STATUS_COLOR[data.status] || 'grey'}>
            {data.status}
          </Label>
        ),
        width: 2,
      },
    ];
  }

  render() {
    const { t } = this.props;
    const {
      docs,
      pages,
      currentPage,
      loading,
      startDate,
      endDate,
      toUser,
      subject,
      org,
      status,
      draftToUser,
      draftSubject,
      draftOrg,
      draftStatus,
      showAdvancedSearch,
      statusOptions,
      selectedLog,
      showInfoDetail,
      showSendTestEmail,
      testEmailTo,
      testEmailSubject,
      sendingTestEmail,
    } = this.state;

    return (
      <Segment
        className="super-admin-content-segment"
        loading={loading}
        disabled={loading}
        attached
      >
        <div className="email-log-filters">
          <label>{t('admin.email_logs.dates')}</label>
          <div className="date-range-container">
            <DateRangeChart
              startDate={startDate}
              endDate={endDate}
              datePicker={this.handleDateRangeChange}
              preDefinedDateRanges
            />
          </div>
          {(startDate || endDate) && (
            <Icon
              name="close"
              link
              className="email-log-clear-date-range"
              onClick={this.clearDateRange}
            />
          )}
          <Button
            className="Primary-Button Medium"
            onClick={this.openAdvancedSearch}
          >
            <div className="add-icon-activity-add">
              <Icon name="search" />
              <div className="add-text">
                {t('admin.email_logs.advanced_search')}
              </div>
            </div>
          </Button>

          {(toUser || subject || org || status.length > 0) && (
            <span className="email-log-active-filters">
              {toUser && (
                <Label size="small">
                  {t('admin.email_logs.to')}: {toUser.email}
                </Label>
              )}
              {subject && (
                <Label size="small">
                  {t('admin.email_logs.subject')}: &quot;{subject}&quot;
                </Label>
              )}
              {org && (
                <Label size="small">
                  {t('common.org_abbrev')}: {org.name}
                </Label>
              )}
              {status.length > 0 && (
                <Label size="small">
                  {t('common.status')}:{' '}
                  {status
                    .map(
                      (s) =>
                        statusOptions.find((o) => o.eventType === s)?.label || s
                    )
                    .join(', ')}
                </Label>
              )}
            </span>
          )}

          <Button
            className="Secondary-Button Medium email-log-send-test-trigger"
            onClick={this.openSendTestEmail}
          >
            <Icon name="mail" />
            {t('admin.email_logs.send_test_email')}
          </Button>

          <Button
            className="Secondary-Button Medium"
            loading={loading}
            disabled={loading}
            onClick={this.refresh}
          >
            <Icon name="refresh" />
            {t('actions.refresh')}
          </Button>
        </div>

        <div className="email-log-table">
          <CustomTable
            header
            columns={this.columns}
            data={docs}
            pagination
            customClass="super-admin-email-log-table tbody-hover"
            handlePaginationChange={this.pageChange}
            page={currentPage}
            noOfPages={pages}
            keyExtractor={(item) => item.id}
            emptyMessage={t('admin.email_logs.no_email_logs_yet')}
            onRowClick={this.openInfoDetail}
          />
        </div>

        <SlideoutPanel
          open={showAdvancedSearch}
          onClose={this.cancelAdvancedSearch}
          title={t('admin.email_logs.email_logs_advanced_search')}
          className="email-log-advanced-search-panel dialog-width-sm"
          actions={
            <Fragment>
              <Button
                className="Primary-Button Medium"
                onClick={this.applyAdvancedSearch}
              >
                {t('actions.apply')}
              </Button>
              <Button
                className="Secondary-Button Medium"
                onClick={this.cancelAdvancedSearch}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                className="Secondary-Button Medium"
                onClick={this.resetAdvancedSearch}
              >
                {t('actions.reset')}
              </Button>
            </Fragment>
          }
        >
          <Form className="email-log-advanced-search-form">
            <Form.Field>
              <label>{t('admin.email_logs.to_user')}</label>
              <UserSearchDropdown
                value={draftToUser}
                onChange={this.handleDraftToUserChange}
                adminSearch
                allowEmailFallback
                placeholder={`${t('admin.email_logs.search_by_recipient_name_or_email')}…`}
              />
            </Form.Field>
            <Form.Field>
              <label>{t('admin.email_logs.subject')}</label>
              <Input
                icon="search"
                placeholder={`${t('admin.email_logs.search_subject')}…`}
                value={draftSubject}
                onChange={this.handleDraftSubjectChange}
                fluid
              />
            </Form.Field>
            <Form.Field>
              <label>{t('common.organisation')}</label>
              <OrganisationDropdown
                value={draftOrg}
                onChange={this.handleDraftOrgChange}
              />
            </Form.Field>
            <Form.Field>
              <label>{t('admin.email_logs.status')}</label>
              <div className="email-log-status-checkboxes">
                {statusOptions.map((statusOption) => (
                  <Checkbox
                    key={statusOption.eventType}
                    label={statusOption.label}
                    checked={draftStatus.includes(statusOption.eventType)}
                    onChange={() =>
                      this.handleDraftStatusToggle(statusOption.eventType)
                    }
                  />
                ))}
              </div>
            </Form.Field>
          </Form>
        </SlideoutPanel>

        <SlideoutPanel
          open={showInfoDetail}
          onClose={this.closeInfoDetail}
          title={t('admin.email_logs.email_logs_details')}
          className="email-log-info-panel dialog-width-lg"
        >
          {selectedLog && (
            <>
              <div className="email-log-info-table-content">
                <div className="email-log-info-field">
                  <label>{t('admin.email_logs.to')}</label>
                  <div>{selectedLog.toEmail || '-'}</div>
                </div>
                <div className="email-log-info-field">
                  <label>{t('admin.email_logs.subject')}</label>
                  <div>{selectedLog.subject || '-'}</div>
                </div>
              </div>
              {selectedLog.html ? (
                <div className="email-log-info-iframe-frame">
                  <EmailHtmlPreview html={selectedLog.html} />
                </div>
              ) : (
                <div className="email-log-info-no-content">
                  {t('admin.email_logs.no_html_content')}
                </div>
              )}
            </>
          )}
        </SlideoutPanel>

        <SlideoutPanel
          open={showSendTestEmail}
          onClose={this.closeSendTestEmail}
          title={t('admin.email_logs.send_test_email')}
          className="email-log-send-test-panel dialog-style1"
          actions={
            <Fragment>
              <Button
                className="Primary-Button Medium"
                loading={sendingTestEmail}
                disabled={sendingTestEmail}
                onClick={this.sendTestEmail}
              >
                {t('actions.send')}
              </Button>
              <Button
                className="Secondary-Button Medium"
                onClick={this.closeSendTestEmail}
              >
                {t('actions.cancel')}
              </Button>
            </Fragment>
          }
        >
          <Form className="email-log-send-test-form">
            <Form.Field>
              <label>{t('admin.email_logs.to')}</label>
              <Input
                type="email"
                placeholder={`${t('admin.email_logs.recipient_email_address')}…`}
                value={testEmailTo}
                onChange={this.handleTestEmailToChange}
                fluid
              />
            </Form.Field>
            <Form.Field>
              <label>{t('admin.email_logs.subject')}</label>
              <Input
                placeholder={`${t('admin.email_logs.subject')}…`}
                value={testEmailSubject}
                onChange={this.handleTestEmailSubjectChange}
                fluid
              />
            </Form.Field>
            <Form.Field>
              <label>{t('admin.email_logs.message')}</label>
              {showSendTestEmail && (
                <CKEditor
                  editor={ClassicEditor}
                  data=""
                  config={{
                    plugins: [
                      Essentials,
                      Bold,
                      Italic,
                      List,
                      Heading,
                      Paragraph,
                      Link,
                    ],
                    toolbar: [
                      'heading',
                      'bold',
                      'italic',
                      'link',
                      'bulletedList',
                      'numberedList',
                    ],
                  }}
                  onChange={this.handleTestEmailMessageChange}
                />
              )}
            </Form.Field>
          </Form>
        </SlideoutPanel>
      </Segment>
    );
  }
}

const mapStateToProps = (state) => ({
  userEmail: state.authentication.user?.email,
});

EmailLogs = withTranslation()(EmailLogs);

export default connect(mapStateToProps)(WithBodyScrollLock(EmailLogs));
